import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { updateStress, useStressById } from 'services/stress';
import 'styles/laudo-base.css';
import { isInfrastructureError } from 'utils/apiErrors';
import { laudos } from 'utils/helpers';
import {
  buildStressPayload,
  getStressRichTextContent,
  hasStoredStressRichText,
  hasStressDocumentContent,
  htmlToStressPlainText,
  isStressLegacyRtf,
  sanitizeStressHtml,
} from './stressRichText';
import { getTemplateHtml, TIPOS_STRESS } from './stressTemplates';

const PLACEHOLDER = '---';

function inferTipoStressByCodProcedi(codProcedi) {
  const cod = Number(codProcedi);

  if (cod === laudos.stress[0]) {
    return TIPOS_STRESS.STRESS_FISIOLOGICO;
  }

  if (cod === laudos.stress[1]) {
    return TIPOS_STRESS.STRESS_FARMACOLOGICO;
  }

  return '';
}

function getTextPosition(root, selection) {
  if (!root || !selection || selection.rangeCount === 0 || !root.contains(selection.anchorNode)) {
    return 0;
  }

  const currentRange = selection.getRangeAt(0);
  const preRange = currentRange.cloneRange();
  preRange.selectNodeContents(root);
  preRange.setEnd(currentRange.endContainer, currentRange.endOffset);
  return preRange.toString().length;
}

function findTextNodeByOffset(root, targetOffset) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let accumulated = 0;
  let currentNode = walker.nextNode();

  while (currentNode) {
    const textLength = currentNode.textContent?.length || 0;
    if (targetOffset <= accumulated + textLength) {
      return {
        node: currentNode,
        offset: Math.max(0, targetOffset - accumulated),
      };
    }

    accumulated += textLength;
    currentNode = walker.nextNode();
  }

  return null;
}

export default function FormStress() {
  const history = useHistory();
  const { id } = useParams();
  const editorRef = useRef(null);
  const initializedIdRef = useRef(null);
  const [tipoStress, setTipoStress] = useState('');
  const [conteudoLaudoHtml, setConteudoLaudoHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const {
    data: stressData,
    isLoading: loading,
    isNotFound,
    error,
    mutate: refetchStress,
  } = useStressById(id);

  const richTextPersisted = hasStoredStressRichText(stressData);
  const isLegacyReadOnly = Boolean(
    stressData?.somenteLeitura ||
      (stressData && !richTextPersisted && isStressLegacyRtf(stressData))
  );
  const stressCode = String(stressData?.CODSTRESS ?? id ?? '--');
  const dataLaudo = String(stressData?.dataFormatada ?? '--');

  const setEditorHtmlImperatively = useCallback((nextHtml) => {
    setConteudoLaudoHtml(nextHtml);
    if (editorRef.current && editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
  }, []);

  useEffect(() => {
    if (!stressData || !id) return;
    if (initializedIdRef.current === String(id)) return;

    const tipoResolvido =
      stressData.TIPOSTRESS || inferTipoStressByCodProcedi(stressData.CODPROCEDI);
    const conteudoPersistido = getStressRichTextContent(stressData);

    const nextHtml =
      isLegacyReadOnly || hasStressDocumentContent(conteudoPersistido)
        ? conteudoPersistido
        : tipoResolvido
          ? getTemplateHtml(tipoResolvido)
          : conteudoPersistido;

    setTipoStress(tipoResolvido || '');
    initializedIdRef.current = String(id);
    setEditorHtmlImperatively(nextHtml);
  }, [stressData, id, isLegacyReadOnly, setEditorHtmlImperatively]);

  // Garante que o DOM do editor receba o HTML inicial após montagem
  useEffect(() => {
    if (!editorRef.current) return;
    if (document.activeElement === editorRef.current) return;

    if (editorRef.current.innerHTML !== conteudoLaudoHtml) {
      editorRef.current.innerHTML = conteudoLaudoHtml;
    }
  }, [conteudoLaudoHtml]);

  const navegarPlaceholder = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection) return;

    const textContent = editor.textContent || '';
    let nextIndex = textContent.indexOf(PLACEHOLDER, getTextPosition(editor, selection));

    if (nextIndex === -1) {
      nextIndex = textContent.indexOf(PLACEHOLDER);
    }

    if (nextIndex === -1) return;

    const start = findTextNodeByOffset(editor, nextIndex);
    const end = findTextNodeByOffset(editor, nextIndex + PLACEHOLDER.length);
    if (!start?.node || !end?.node) return;

    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);

    selection.removeAllRanges();
    selection.addRange(range);
    editor.focus();
  }, []);

  const applyCommand = useCallback(
    (command, value = null) => {
      if (isLegacyReadOnly || !editorRef.current) return;

      editorRef.current.focus();
      document.execCommand(command, false, value);
      setConteudoLaudoHtml(editorRef.current.innerHTML);
    },
    [isLegacyReadOnly]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'F2') {
        event.preventDefault();
        navegarPlaceholder();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey) {
        const key = event.key.toLowerCase();
        if (key === 'b') {
          event.preventDefault();
          applyCommand('bold');
        } else if (key === 'i') {
          event.preventDefault();
          applyCommand('italic');
        } else if (key === 'u') {
          event.preventDefault();
          applyCommand('underline');
        }
      }
    },
    [navegarPlaceholder, applyCommand]
  );

  const handleTipoChange = (event) => {
    if (isLegacyReadOnly) return;

    const novoTipo = event.target.value;
    setTipoStress(novoTipo);

    if (novoTipo) {
      setEditorHtmlImperatively(getTemplateHtml(novoTipo));
    }
  };

  const syncEditorState = useCallback(() => {
    if (!editorRef.current) return;
    setConteudoLaudoHtml(editorRef.current.innerHTML);
  }, []);

  const handleEditorBlur = useCallback(() => {
    if (!editorRef.current) return;

    const sanitized = sanitizeStressHtml(editorRef.current.innerHTML);
    if (editorRef.current.innerHTML !== sanitized) {
      editorRef.current.innerHTML = sanitized;
    }
    setConteudoLaudoHtml(sanitized);
  }, []);

  const handleGravar = async () => {
    if (isLegacyReadOnly) return;

    const currentHtml = editorRef.current ? editorRef.current.innerHTML : conteudoLaudoHtml;

    if (!tipoStress) {
      toast.warn('Selecione o tipo de stress');
      return;
    }

    if (!hasStressDocumentContent(currentHtml)) {
      toast.warn('Preencha o conteúdo do laudo');
      return;
    }

    const payload = buildStressPayload({
      tipoStress,
      htmlContent: currentHtml,
      stressData,
    });

    setSaving(true);
    try {
      if (id) {
        await updateStress(id, payload);
        await refetchStress();
        toast.success('Laudo atualizado com sucesso!');
      }
    } catch {
      toast.error('Erro ao gravar laudo');
    } finally {
      setSaving(false);
    }
  };

  const handleGerarPDF = () => {
    const currentHtml = editorRef.current ? editorRef.current.innerHTML : conteudoLaudoHtml;
    if (!id && !hasStressDocumentContent(currentHtml)) {
      toast.warn('Salve o laudo antes de gerar o PDF');
      return;
    }

    history.push({
      pathname: `/visualizacao-stress/${id}`,
    });
  };

  const handleCancelar = () => {
    history.goBack();
  };

  if (loading && !stressData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '400px' }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (isNotFound && !stressData) {
    return (
      <Alert variant="warning" className="m-3">
        O registro de Eco Stress com ID <strong>{id}</strong> não foi encontrado no sistema.
      </Alert>
    );
  }

  const hasError = Boolean(error);
  const isInfraError = isInfrastructureError(error);

  if (hasError && !stressData) {
    return isInfraError ? (
      <ServiceUnavailableAlert error={error} onRetry={() => refetchStress()} />
    ) : (
      <Alert variant="danger" className="m-3 text-center">
        <p className="mb-2">{error?.message || 'Erro ao carregar o laudo de Eco Stress.'}</p>
        <Button variant="outline-danger" size="sm" onClick={() => refetchStress()}>
          Tentar novamente
        </Button>
      </Alert>
    );
  }

  const conteudoLaudoTexto = htmlToStressPlainText(conteudoLaudoHtml);

  return (
    <Card className="mb-2 mt-2 no-print legacy-eco-card">
      <Card.Header className="legacy-header">
        <div className="legacy-header-side">
          <div className="legacy-header-eco-wrap">
            <span className="legacy-mini-label">Stress N:</span>
            <span className="legacy-mini-value">{stressCode}</span>
          </div>
        </div>

        <div className="legacy-header-title-wrap">
          <div className="legacy-header-title">Eco Stress</div>
        </div>

        <div className="legacy-header-side legacy-header-side-right">
          <div className="legacy-header-date-wrap">
            <span className="legacy-mini-label">Data:</span>
            <span className="legacy-mini-value">{dataLaudo}</span>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="legacy-card-body">
        <div className="legacy-content-shell container-fluid px-0">
          {hasError &&
            stressData &&
            (isInfraError ? (
              <ServiceUnavailableAlert error={error} stale onRetry={() => refetchStress()} />
            ) : (
              <Alert variant="warning" className="mb-3">
                Não foi possível atualizar os dados mais recentes.{' '}
                <Button variant="link" size="sm" className="p-0" onClick={() => refetchStress()}>
                  Tentar novamente
                </Button>
              </Alert>
            ))}

          <div className="legacy-section-title-wrap">
            <div className="legacy-section-title">LAUDO DE ECOCARDIOGRAMA DE STRESS</div>
          </div>

          {stressData && (
            <div className="row g-3 legacy-panel-grid mb-3">
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Paciente</div>
                  <div className="legacy-field-value">
                    {stressData?.CODPAC || '--'} - {stressData?.NOMEPAC || '--'}
                  </div>
                </div>
              </div>
              <div className="col-lg-2">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Sexo</div>
                  <div className="legacy-field-value">{stressData?.SEXOPAC || '--'}</div>
                </div>
              </div>
              <div className="col-lg-2">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Data Nasc</div>
                  <div className="legacy-field-value">{stressData?.dataNasc || '--'}</div>
                </div>
              </div>
              <div className="col-lg-2">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Idade</div>
                  <div className="legacy-field-value">
                    {stressData?.IDADEPAC ? `${stressData.IDADEPAC} anos` : '--'}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Médico Realizou</div>
                  <div className="legacy-field-value">{stressData?.Realizou || '--'}</div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="legacy-field-block h-100">
                  <div className="legacy-field-label">Médico Solicitou</div>
                  <div className="legacy-field-value">{stressData?.Solicitou || '--'}</div>
                </div>
              </div>
            </div>
          )}

          {isLegacyReadOnly && (
            <Alert variant="info" className="border mb-3">
              Laudo originado no Delphi (RTF). Nesta tela ele só pode ser visualizado, para não
              alterar o formato antigo.
            </Alert>
          )}

          {richTextPersisted && !isLegacyReadOnly && (
            <Alert variant="success" className="border mb-3">
              Laudo criado na web. A gravação fica neste sistema e não é enviada ao Delphi.
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              <strong>Tipo de Stress</strong>
            </Form.Label>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '1.5rem' }}>
              {Object.values(TIPOS_STRESS).map((tipo) => (
                <Form.Check
                  key={tipo}
                  type="radio"
                  id={`tipo-stress-${tipo}`}
                  label={tipo}
                  name="tipoStress"
                  checked={tipoStress === tipo}
                  onChange={() => handleTipoChange({ target: { value: tipo } })}
                  disabled={isLegacyReadOnly}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label>
              <strong>Conteúdo do Laudo</strong>
              {!isLegacyReadOnly && (
                <small className="text-muted ms-2">(F2 = próximo campo ---)</small>
              )}
            </Form.Label>

            {!isLegacyReadOnly && (
              <div className="border rounded p-2 mb-2 d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyCommand('bold')}
                  title="Negrito (Ctrl+B)"
                >
                  <strong>Negrito</strong>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyCommand('italic')}
                  title="Itálico (Ctrl+I)"
                >
                  <em>Itálico</em>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyCommand('underline')}
                  title="Sublinhado (Ctrl+U)"
                >
                  <u>Sublinhado</u>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => applyCommand('insertUnorderedList')}
                >
                  Lista
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={navegarPlaceholder}
                  title="Selecionar próximo --- (F2)"
                >
                  Próximo campo (F2)
                </Button>
              </div>
            )}

            <div
              ref={editorRef}
              contentEditable={!isLegacyReadOnly}
              suppressContentEditableWarning
              className="form-control stress-rich-editor"
              onInput={syncEditorState}
              onBlur={handleEditorBlur}
              onKeyDown={handleKeyDown}
              style={{
                minHeight: '520px',
                fontSize: '14px',
                lineHeight: '1.55',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: '"Courier New", Courier, monospace',
                backgroundColor: isLegacyReadOnly ? '#f8f9fa' : '#ffffff',
              }}
            />

            <Form.Text className="text-muted d-block mt-2">
              {conteudoLaudoTexto ? conteudoLaudoTexto.split('\n').length : 0} linhas.
            </Form.Text>
          </Form.Group>
        </div>
      </Card.Body>

      <Card.Footer className="legacy-footer no-print text-center">
        <Button variant="outline-secondary" onClick={handleCancelar}>
          Cancelar
        </Button>{' '}
        <Button
          variant="outline-info"
          onClick={handleGerarPDF}
          disabled={!hasStressDocumentContent(conteudoLaudoHtml)}
        >
          Visualizar
        </Button>
        {!isLegacyReadOnly && (
          <>
            {' '}
            <Button variant="success" onClick={handleGravar} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : 'Gravar'}
            </Button>
          </>
        )}
      </Card.Footer>
    </Card>
  );
}
